// components/auth/segmented-digit-input.tsx
// A row of single-digit boxes (employee number, OTP code) that behaves like
// one field: typing auto-advances, backspace on an empty box steps back and
// clears the previous one, arrow keys move focus, and pasting a full code
// anywhere in the row distributes it across every box at once -- the same
// interaction every OTP input on the web already uses. Digits only; any
// other character is dropped, never shown.
"use client";

import { cn } from "@/lib/utils";
import { useRef } from "react";

export function SegmentedDigitInput({
  length,
  value,
  onChange,
  autoFocus = false,
  disabled = false,
  ariaLabel,
  onComplete,
}: {
  length: number;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  /** Fires once, the instant the value reaches `length` digits (e.g. auto-submit). */
  onComplete?: (value: string) => void;
}) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.replace(/\D/g, "").slice(0, length);

  const commit = (next: string) => {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
  };

  const focusBox = (index: number) => {
    inputsRef.current[index]?.focus();
    inputsRef.current[index]?.select();
  };

  const handleChange = (index: number, raw: string) => {
    // A single keystroke normally lands here as one character, but some
    // mobile keyboards (and browser autofill) can hand back the whole
    // string in one go -- treat any multi-char input the same as a paste.
    const typed = raw.replace(/\D/g, "");
    if (typed.length > 1) {
      const merged = (digits.slice(0, index) + typed).slice(0, length);
      commit(merged);
      focusBox(Math.min(merged.length, length - 1));
      return;
    }
    const nextDigits = digits.slice(0, index) + typed + digits.slice(index + 1);
    commit(nextDigits);
    if (typed && index < length - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        // Clearing a filled box: let the change handler do it naturally.
        return;
      }
      e.preventDefault();
      if (index > 0) {
        commit(digits.slice(0, index - 1) + digits.slice(index));
        focusBox(index - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusBox(index + 1);
    } else if (e.key === "Enter" && digits.length === length) {
      // All boxes are already filled: let Enter submit immediately instead
      // of forcing a click on the Next/Sign in button.
      e.preventDefault();
      onComplete?.(digits);
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    const merged = (digits.slice(0, index) + pasted).slice(0, length);
    commit(merged);
    focusBox(Math.min(merged.length, length - 1));
  };

  // A 6-box row (the OTP code) is noticeably wider than a 4-box row (the
  // employee number) at the same box size -- wide enough to crowd or
  // overflow the card's own padding. Scale the box down a step once
  // there are more than 4 of them, rather than fixing the width for
  // every length.
  const compact = length > 4;

  return (
    <div className={cn("flex", compact ? "gap-1.5" : "gap-2")} dir="ltr" role="group" aria-label={ariaLabel}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digits[i] ?? ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={ariaLabel ? `${ariaLabel} ${i + 1}` : undefined}
          className={cn(
            "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-center font-medium text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50",
            compact ? "h-10 w-9 text-base" : "h-11 w-11 text-lg",
          )}
        />
      ))}
    </div>
  );
}
